import { Space } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import type { ComponentProps } from 'react';
import { useCallback, useEffect } from 'react';

import { QUERY_PARAMS } from 'constants/queryNames';
import { APP_PAGES_NAMES } from 'constants/routes';

import { FormStepId } from 'shared/config/formStepId';
import { useAppSelector } from 'shared/lib';
import { sendEventOnChangeFilter, sendEventOnSubmitOffer, sendEventShowLandingPage } from 'shared/lib/sendGAEvents';
import { AppContainer } from 'shared/ui/AppContainer';

import { hasAnySeoDataSelector, SeoBlocks, SeoBreadcrumbs, SeoPrefooter } from 'entities/metadata';

import { useClientRedirect } from 'features/useClientRedirect';

import { LoansHeroFormSection } from 'widgets/LoansHeroFormSection';
import { VitrinsOffers } from 'widgets/VitrinsOffers';

import { LoansLandingPageTexts } from '../LoansLandingPage.texts';

import styles from './LoansLandingPage.module.scss';

export const LoansLandingPage = () => {
  const hasAnySeoData = useAppSelector(hasAnySeoDataSelector);
  const isMobile = useIsMobile();
  const blockSpacing = isMobile ? 32 : 48;
  const { push, prefetch } = useClientRedirect();

  useEffect(() => {
    sendEventShowLandingPage();
    prefetch(APP_PAGES_NAMES.NEW_FORM);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeFilter: NonNullable<ComponentProps<typeof VitrinsOffers>['onChangeFilter']> = useCallback(
    ({ name }) => {
      sendEventOnChangeFilter(name);
    },
    [],
  );

  const onOfferSubmit: NonNullable<ComponentProps<typeof VitrinsOffers>['onOfferSubmit']> = useCallback(
    async (item) => {
      const { title } = item;
      sendEventOnSubmitOffer(title || '');

      const params = new URLSearchParams(window.location.search);
      params.set(QUERY_PARAMS.FORM_STEP_ID, String(FormStepId.ContactsInfo));

      await push(APP_PAGES_NAMES.NEW_FORM, params.toString());
    },
    [push],
  );

  return (
    <Space
      direction="vertical"
      size={isMobile ? 16 : 32}
    >
      <LoansHeroFormSection />
      <Space
        direction="vertical"
        size={blockSpacing}
        className={styles.blocks}
      >
        <VitrinsOffers
          title={LoansLandingPageTexts.OffersTitle}
          onChangeFilter={onChangeFilter}
          submitButtonText={LoansLandingPageTexts.OfferSubmitButtonText}
          onOfferSubmit={onOfferSubmit}
        />
      </Space>
      {hasAnySeoData && (
        <AppContainer
          hidePaddings
          className={styles.seo}
        >
          <SeoBlocks />
          <SeoPrefooter />
          <div className={isMobile ? 'h-ml-16' : undefined}>
            <SeoBreadcrumbs />
          </div>
        </AppContainer>
      )}
    </Space>
  );
};
